export type DateRangeSearchPreset = 'today' | 'week' | '7d' | '30d' | 'mtd' | 'ytd' | 'custom';

export interface DateRangeSearchValue {
  preset: DateRangeSearchPreset;
  from?: string;
  to?: string;
}

const PRESETS = new Set<DateRangeSearchPreset>([
  'today',
  'week',
  '7d',
  '30d',
  'mtd',
  'ytd',
  'custom',
]);

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A day key that names a day that exists. The shape check alone lets through
 * `2026-02-31`, which parses to nothing downstream and leaves a range with one
 * open end.
 */
export function isValidDateKey(value: string) {
  if (!DATE_KEY.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

export function readDateRangeSearch(): DateRangeSearchValue | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const range = params.get('range');
  if (!range || !PRESETS.has(range as DateRangeSearchPreset)) return null;

  const preset = range as DateRangeSearchPreset;
  if (preset !== 'custom') return { preset };

  const from = params.get('from');
  const to = params.get('to');
  if (!from || !to || !isValidDateKey(from) || !isValidDateKey(to) || from > to) return null;
  return { preset, from, to };
}

export function replaceDateRangeSearch(value: DateRangeSearchValue) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('range', value.preset);
  if (value.preset === 'custom' && value.from && value.to) {
    url.searchParams.set('from', value.from);
    url.searchParams.set('to', value.to);
  } else {
    url.searchParams.delete('from');
    url.searchParams.delete('to');
  }
  window.history.replaceState(window.history.state, '', url);
}
